/**
 * THE COVERAGE PANEL — "is every client accounted for?", answered arithmetically.
 *
 * This replaces two things that sat at the top of the Ask queue:
 *
 *   1. A red headline reading "2,178 · asks never made". Measured against the
 *      live board, most of that number is orders scored past-window off a BULK
 *      STATUS EDIT timestamp rather than a delivery (see reviewCoverage.js).
 *      So the headline no longer prints that figure as a failure until it has
 *      actually been broken down — and while it is loading, or if the breakdown
 *      fails, it says what it does not yet know instead of guessing high.
 *
 *   2. A flat `.tally` row mixing the eight exclusive states with the two
 *      overlays at one weight, omitting `notDelivered` entirely. Eight numbers
 *      that read as a partition, could not be summed, and left out the biggest
 *      answer to "why isn't my client here".
 *
 * The overlays are rendered in their own block, after the partition, under a
 * heading that says they are counted inside it. That separation is the whole
 * point — putting them in the same row is what made them look like buckets.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../../lib/api.js'
import {
  coverageTally, splitMissed, dayClusters, CLUSTER_MIN,
  MISSED_KINDS, MISSED_UNASKED, MISSED_NO_REAL_DATE,
  holdSummary, NO_RISK_SIGNAL,
} from './reviewCoverage.js'
import s from './reviews.module.css'

const n = (v) => (v || 0).toLocaleString()

export default function CoveragePanel({ tally, queue, scope }) {
  const [open, setOpen] = useState(false)
  // null = not attempted · 'loading' · 'error' · an array of rows
  const [missed, setMissed] = useState(null)
  const [missedErr, setMissedErr] = useState(null)

  const cov = useMemo(() => coverageTally(tally), [tally])

  /* The missed rows are a SECOND request and deliberately so: the queue route
   * returns counts only, and the firm/inferred flag this whole restatement
   * turns on lives on the ROW. `?state=missed` is an existing view on an
   * existing route — no backend change (hard rule #4). */
  const loadMissed = useCallback(async () => {
    setMissed('loading'); setMissedErr(null)
    try {
      const d = await api.reviewQueue({ state: 'missed' })
      setMissed(Array.isArray(d?.queue) ? d.queue : [])
    } catch (e) {
      setMissed('error')
      setMissedErr(e?.message || 'Could not read the past-window orders.')
    }
  }, [])

  useEffect(() => { loadMissed() }, [loadMissed, scope])

  const rows = Array.isArray(missed) ? missed : null
  const split = useMemo(() => (rows ? splitMissed(rows) : null), [rows])
  const clusters = useMemo(
    () => (split ? dayClusters(split.noRealDate) : null),
    [split]
  )
  const holds = useMemo(() => holdSummary(queue || []), [queue])

  const missedTotal = cov.rows.find((r) => r.key === 'missed')?.count || 0

  return (
    <section className={s.coverage} aria-label="Review coverage">

      {/* ── THE HEADLINE ──────────────────────────────────────────────────
          The figure is the RESTATED one. Until the breakdown lands there is
          no figure at all, because the only number available at that moment
          is the one that was wrong. */}
      {split ? (
        <div className={s.headline}>
          <div className={s.headlineFig}>{n(split.counts[MISSED_UNASKED])}</div>
          <div className={s.headlineBody}>
            <h2 className={s.headlineTitle}>asks never made</h2>
            <p className={s.headlineWhy}>
              {MISSED_KINDS[MISSED_UNASKED].why}{' '}
              {split.counts[MISSED_NO_REAL_DATE] > 0 && (
                <>
                  A further <b>{n(split.counts[MISSED_NO_REAL_DATE])}</b> orders sit past
                  the window but carry <b>no real delivery date</b>, so they are not
                  counted here and are not anybody’s failure — see below.
                </>
              )}
            </p>
          </div>
        </div>
      ) : (
        <div className={`${s.headline} ${s.headlineQuiet}`}>
          <div className={s.headlineFig}>{n(missedTotal)}</div>
          <div className={s.headlineBody}>
            <h2 className={s.headlineTitle}>delivered past the window</h2>
            <p className={s.headlineWhy}>
              {missed === 'loading' && 'Breaking these down — most of this number is not what it looks like, so it is deliberately not being called “asks never made” until it has been split.'}
              {missed === 'error' && (
                <>
                  <strong>The breakdown failed, so this figure is NOT a count of missed
                  asks.</strong> Most past-window orders carry no real delivery date and
                  were aged off an administrative timestamp. {missedErr}{' '}
                  <button type="button" className={s.linkBtn} onClick={loadMissed}>Try again</button>
                </>
              )}
              {missed === null && 'Not yet broken down.'}
            </p>
          </div>
        </div>
      )}

      {/* ── THE PARTITION ─────────────────────────────────────────────── */}
      <button
        type="button"
        className={s.coverageToggle}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Hide' : 'Show'} where all {n(cov.accounted)} orders are —
        every client in exactly one bucket
      </button>

      {open && (
        <div className={s.coverageBody}>
          {cov.groups.map((g) => (
            <div key={g.key} className={s.covGroup}>
              <div className={s.covGroupHead}>
                <span className={s.covGroupLabel}>{g.label}</span>
                <span className={s.covGroupCount}>{n(g.count)}</span>
              </div>
              <p className={s.covGroupBlurb}>{g.blurb}</p>
              <ul className={s.covList}>
                {g.states.map((st) => (
                  <li key={st.key} className={s.covRow} data-state={st.key}>
                    <span className={s.covCount}>{n(st.count)}</span>
                    <span className={s.covLabel}>{st.label}</span>
                    <span className={s.covWhy}>{st.why}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* ── THE OVERLAYS, VISIBLY APART ───────────────────────────────
              Same data the old flat row carried, but it can no longer be read
              as part of the sum, because it is not in the sum. */}
          <div className={`${s.covGroup} ${s.covOverlay}`}>
            <div className={s.covGroupHead}>
              <span className={s.covGroupLabel}>Overlays — already counted above</span>
            </div>
            <p className={s.covGroupBlurb}>
              These are <strong>not</strong> extra buckets. Each of these orders is already
              counted inside one of the states above; this says something additional about
              it. Adding them to the total double-counts.
            </p>
            <ul className={s.covList}>
              {cov.overlays.map((o) => (
                <li key={o.key} className={s.covRow}>
                  <span className={s.covCount}>{n(o.count)}</span>
                  <span className={s.covLabel}>{o.label}</span>
                  <span className={s.covWhy}>{o.why}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* ── THE FOOTER IS AN ASSERTION, NOT A SENTENCE ────────────────
              It used to be possible to print "nothing is hidden" whether or not
              it was true. This states the property that was actually checked
              and fails loudly by name. */}
          {cov.reconciled ? (
            <p className={s.covFooter}>
              Reconciled — all <b>{n(cov.accounted)}</b> orders are drawn in exactly one
              bucket above, every state the backend reported has a home here, and neither
              overlay exceeds the total.
              {!cov.totalChecked && ' (Checked against the states the queue reported; the route does not return an independent universe size.)'}
            </p>
          ) : (
            <p className={`${s.covFooter} ${s.covFooterBad}`} role="alert">
              <strong>RECONCILIATION FAILED.</strong>{' '}
              {cov.missing !== 0 && <>The buckets are short by <b>{n(Math.abs(cov.missing))}</b> orders. </>}
              {cov.stray.length > 0 && (
                <>The queue reported {cov.stray.length === 1 ? 'a state' : 'states'} this
                screen cannot place: {cov.stray.map((x) => `${x.key} (${n(x.count)})`).join(', ')}. </>
              )}
              {cov.overlayProblems.length > 0 && (
                <>{cov.overlayProblems.map((x) => `${x.label} (${n(x.count)})`).join(', ')}{' '}
                exceeds the whole book, so the two numbers are not describing the same set. </>
              )}
              Do not treat the counts above as complete.
            </p>
          )}
        </div>
      )}

      {/* ── THE RESTATED MISS, WITH ITS RECEIPT ───────────────────────── */}
      {split && (
        <div className={s.covMissed}>
          <h3 className={s.covMissedTitle}>
            The {n(split.total)} past the window, split
          </h3>
          <ul className={s.covList}>
            {[MISSED_UNASKED, MISSED_NO_REAL_DATE].map((k) => (
              <li key={k} className={s.covRow} data-missed={k}>
                <span className={s.covCount}>{n(split.counts[k])}</span>
                <span className={s.covLabel}>{MISSED_KINDS[k].label}</span>
                <span className={s.covWhy}>{MISSED_KINDS[k].why}</span>
              </li>
            ))}
          </ul>

          {/* The evidence, computed live from the same rows — so the claim is
              checkable on screen rather than taken on trust. It is deliberately
              NOT what decides the split; the structural firm/inferred flag is. */}
          {clusters && clusters.clusters.length > 0 && (
            <p className={s.covEvidence}>
              <strong>Why those are discounted:</strong> their delivery dates stack.{' '}
              {clusters.clusters.slice(0, 3).map((c) => `${n(c.count)} on ${c.day}`).join(', ')}
              {clusters.clusters.length > 3 && `, and ${clusters.clusters.length - 3} more such ${clusters.clusters.length - 3 === 1 ? 'day' : 'days'}`}
              {' '}— {n(clusters.clustered)} of {n(split.counts[MISSED_NO_REAL_DATE])} fall on just{' '}
              {clusters.clusters.length} {clusters.clusters.length === 1 ? 'day' : 'days'} carrying{' '}
              {CLUSTER_MIN}+ orders each. Nobody delivers hundreds of orders on one day and
              none the next week; that is a status change being applied in bulk.
            </p>
          )}
          {!split.reconciled && (
            <p className={`${s.covFooter} ${s.covFooterBad}`} role="alert">
              <strong>RECONCILIATION FAILED.</strong> The two halves do not sum to the{' '}
              {n(split.total)} rows read.
            </p>
          )}
        </div>
      )}

      {/* ── HOLD ──────────────────────────────────────────────────────── */}
      {holds.length > 0 && (
        <div className={s.covHold}>
          <h3 className={s.covMissedTitle}>Held — we cannot address these confidently</h3>
          <p className={s.covGroupBlurb}>
            Still yours and still in the list below; this is a missing field, not a
            judgement about the customer, and it is <strong>not</strong> a do-not-ask —
            that is a decision only a person makes, with a reason.
          </p>
          <ul className={s.covList}>
            {holds.map((h) => (
              <li key={h.key} className={s.covRow}>
                <span className={s.covCount}>{n(h.count)}</span>
                <span className={s.covLabel}>{h.label}</span>
                <span className={s.covWhy}>{h.fix}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* The standing statement, verbatim from the model. */}
      <p className={s.covNoSignal}>{NO_RISK_SIGNAL}</p>
    </section>
  )
}
